"use client";

import Link from "next/link";
import Image from "next/image";
import { useRef } from "react";
import useScrollDirection from "./useScrollDirection";
import { usePathname } from "next/navigation";
import { Search } from "lucide-react";
import { useBannerContext } from "@core/helpers/client/contexts/BannerContext";
import { useWebSiteContext } from "@core/helpers/client/contexts/WebSiteContext";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import { build_seller_request_href } from "@/helpers/sellerRequest";
import useSellerPanel, { PANEL_LOGIN_URL } from "@/hooks/useSellerPanel";
import BannerSection, { should_show_banner } from "./_banner";
import { useInitialBanners } from "@/contexts/InitialBanners";
import NavSellerAcademy from "./_navSellerAcademy";
import Button from "@/components/ui/button";
import useIsDesktop from "@/hooks/useIsDesktop";
import useResizeMeasure from "@/hooks/useResizeMeasure";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";


export default function HeaderSellerAcademy({ banner_count = 0 }) {
    const { banner_data, banner_loading } = useBannerContext();
    const { menus_loading } = useMenusContext();
    const show_banner = should_show_banner({ banner_data, banner_loading, initial_count: banner_count, initial_banners: useInitialBanners() });
    const { web_route_names } = useWebSiteContext();
    const { is_auth } = useAuthContext();
    const pathname = usePathname() || "";
    const search_href = web_route_names?.["search-seller-academy"] || "/seller-academys";
    // صفحه‌ی فعلی همراه لینک می‌رود تا «انصراف» کاربر را به همین‌جا برگرداند.
    const seller_request_href = build_seller_request_href(web_route_names, pathname);
    const { can_access_panel, open_panel, opening_panel } = useSellerPanel();
    const headerRef = useRef(null);
    // SSR و hydration هر دو نسخه را دارند؛ بعد از آن فقط نسخه‌ی همان viewport در DOM می‌ماند
    const is_desktop = useIsDesktop();
    // ردیف دوم دسکتاپ مثل هدر مجله با اسکرول به پایین بسته و با اسکرول به بالا باز می‌شود
    const showNavbar = useScrollDirection();

    // مهمان اول لاگین می‌کند و مودال بعد از ورود خودش به استپ اول می‌برد. کلیک
    // با کلید کمکی یا دکمه‌ی وسط به مرورگر سپرده می‌شود؛ خود صفحه هم برای مهمان
    // مودال ورود را باز می‌کند.
    const handle_seller_request_click = (event) => {
        if (is_auth) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        openAuthModal(seller_request_href);
    };

    // درخواستِ تأییدشده: ادمین در پنل دسترسی فروشنده داده و همان flagِ منوی
    // پروفایل (`can_access_panel`) true شده است؛ دیگر فرم درخواست معنی ندارد.
    // عرض دکمه باید دقیقا همان «درخواست فروشندگی» بماند، پس متن قبلی نامرئی در
    // همان خانه‌ی گرید می‌ماند و عرض را نگه می‌دارد. اسپینر loading هم عمدا
    // استفاده نشده چون جای متن را می‌گیرد و دکمه را کوچک می‌کند؛ open_panel خودش
    // جلوی کلیک دوباره را می‌گیرد.
    const header_action_props = can_access_panel
        ? {
            href: PANEL_LOGIN_URL,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: open_panel,
            "aria-busy": opening_panel || undefined,
            text: (
                <span className="grid justify-items-center">
                    <span className="col-start-1 row-start-1">پنل فروشندگان</span>
                    <span aria-hidden="true" className="invisible col-start-1 row-start-1">درخواست فروشندگی</span>
                </span>
            ),
        }
        : {
            href: seller_request_href,
            onClick: handle_seller_request_click,
            text: "درخواست فروشندگی",
        };

    // ارتفاع هدر فقط در ResizeObserver خوانده می‌شود (useResizeMeasure)؛ تغییر اندازه‌ی پنجره هم از همین‌جا می‌رسد
    useResizeMeasure(
        () => [headerRef.current],
        () => {
            if (!headerRef.current) return;
            const height = headerRef.current.getBoundingClientRect().height;
            return () => document.documentElement.style.setProperty("--header-height", `${height}px`);
        },
        [banner_data, is_desktop]
    );

    return (
        <header dir="rtl" className="relative w-full bg-jmt-white">
            <div ref={headerRef} className="fixed top-0 right-0 left-0 z-40 bg-jmt-white">

                {show_banner && <BannerSection initial_count={banner_count} />}

                {/* mobile — جستجو مطابق فیگما به نویگیشن پایین (FooterNavigationSellerAcademy) رفته است */}
                {is_desktop !== true && <div className="border-b border-jmt-neutral-200 lg:hidden">
                    <div className="container">
                        <div className="flex w-full items-center justify-between gap-4 py-2.5">

                            <Link href="/seller-academy" className="flex shrink-0 h-6 w-auto">
                                <Image
                                    src="/images/logo/academy-logo.png"
                                    unoptimized
                                    alt="سلر آکادمی ژومنت"
                                    width={150}
                                    height={40}
                                    priority
                                    // w-full داخل لینکِ w-auto در Safari به عرض طبیعی عکس می‌رسد و دکمه را از صفحه بیرون می‌زند
                                    className="object-contain h-full w-auto"
                                />
                            </Link>

                            <div className="flex min-h-10 items-center justify-end gap-2">
                                <Button
                                    {...header_action_props}
                                    color_type="primary"
                                    className="h-10! shrink-0 whitespace-nowrap px-4! py-0!"
                                />
                            </div>

                        </div>
                    </div>
                </div>}

                {/* desktop */}
                {is_desktop !== false && <div className="hidden lg:block">
                    <div className="border-b border-jmt-neutral-100 py-4">
                        <div className="container">
                            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-4">

                                <Link href="/" className="justify-self-start text-sm font-bold text-jmt-primary-800 hover:text-jmt-primary-900">
                                    رفتن به فروشگاه ژومنت
                                </Link>

                                <Link href="/seller-academy" className="flex">
                                    <div className="w-[226px] h-10">
                                        <Image
                                            src="/images/logo/academy-logo.png"
                                            unoptimized
                                            alt="سلر آکادمی ژومنت"
                                            width={226}
                                            height={40}
                                            priority
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                </Link>

                                <div className="flex items-center justify-end gap-4">
                                    <Link
                                        href={search_href}
                                        aria-label="جستجو در سلر آکادمی"
                                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition hover:bg-jmt-neutral-100">
                                        <Search size={24} className="text-jmt-neutral-800" />
                                    </Link>
                                    <Button
                                        {...header_action_props}
                                        color_type="primary"
                                        className="shrink-0 whitespace-nowrap px-3! py-3!"
                                    />
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className={`overflow-hidden border-b border-jmt-neutral-100 transition-all duration-500 ease-in-out ${showNavbar ? "max-h-20 opacity-100" : "max-h-0 opacity-0"}`}>
                        {menus_loading ?
                            <nav className="flex items-center justify-center gap-6 py-4">
                                {[...Array(3)].map((_, index) => (
                                    <div key={index} className="h-6 w-[110px] rounded-md bg-jmt-neutral-100" />
                                ))}
                            </nav> : <NavSellerAcademy />}
                    </div>
                </div>}
            </div>
        </header>
    );
}
